/**
 * @Module: Kitchen Types
 * @Status: Ready
 * @Summary: TypeScript types for the kitchen profile system
 * @Description: Pure data types mirroring the backend KitchenProfile schema.
 *   Used by KitchenContext, KitchenSetup, and the API service layer.
 * @NextSteps: Add zod validation, profile versioning
 */

export interface KitchenProfile {
  cookware: string[];
  appliances: string[];
  tools: string[];
  pantryStaples: string[];
  dietary: string[];
  skillLevel: string;
  householdSize: number;
  notes: string;
  rawText: string;
}

export interface KitchenProfileResponse extends KitchenProfile {
  warnings: string[];
}

/** Empty profile — used as default before the user sets one up. */
export function emptyKitchenProfile(): KitchenProfile {
  return {
    cookware: [],
    appliances: [],
    tools: [],
    pantryStaples: [],
    dietary: [],
    skillLevel: 'intermediate',
    householdSize: 1,
    notes: '',
    rawText: '',
  };
}

/**
 * Convert backend snake_case response to frontend camelCase profile.
 * The backend uses pantry_staples / skill_level / household_size / raw_text.
 */
export function profileFromApi(data: Record<string, any>): KitchenProfileResponse {
  return {
    cookware: data.cookware ?? [],
    appliances: data.appliances ?? [],
    tools: data.tools ?? [],
    pantryStaples: data.pantry_staples ?? [],
    dietary: data.dietary ?? [],
    skillLevel: data.skill_level ?? 'intermediate',
    householdSize: data.household_size ?? 1,
    notes: data.notes ?? '',
    rawText: data.raw_text ?? '',
    warnings: data.warnings ?? [],
  };
}

/**
 * Convert frontend camelCase profile to backend snake_case for API calls.
 */
export function profileToApi(profile: KitchenProfile): Record<string, any> {
  return {
    cookware: profile.cookware,
    appliances: profile.appliances,
    tools: profile.tools,
    pantry_staples: profile.pantryStaples,
    dietary: profile.dietary,
    skill_level: profile.skillLevel,
    household_size: profile.householdSize,
    notes: profile.notes,
    raw_text: profile.rawText,
  };
}
